import React from 'react';
import HomePage from '../../pages/HomePage';
import GoalsPage from '../../pages/GoalsPage';
import { Goal, SubGoal, Todo } from '../../services/goalService';
import { AIGoalPlan } from '../../services/aiService';
import { HashRouteInfo } from '../../routing/parseHashRoute';
import { TabType } from '../../hooks/useNavigation';

const AIPlanDraftsPage = React.lazy(() => import('../../pages/AIPlanDraftsPage/AIPlanDraftsPage'));
const GoalDetailPage = React.lazy(() => import('../../pages/GoalDetailPage'));
const SubGoalTodosPage = React.lazy(() => import('../../pages/SubGoalTodosPage'));
const TodoDetailPage = React.lazy(() => import('../../pages/TodoDetailPage'));
const OngoingPage = React.lazy(() => import('../../pages/OngoingPage'));
const TimelyPage = React.lazy(() => import('../../pages/TimelyPage'));
const AchievementsPage = React.lazy(() => import('../../pages/AchievementsPage'));
const ProfilePage = React.lazy(() => import('../../pages/ProfilePage'));
const SettingsPage = React.lazy(() => import('../../pages/SettingsPage'));

export interface AppRoutesProps {
  route: HashRouteInfo;
  activeTab: TabType | null;
  goals: Goal[];
  loading: boolean;
  error: string | null;
  loadGoals: () => Promise<void>;
  updateGoal: (goalId: string, updates: Partial<Goal>) => Promise<void>;
  deleteGoal: (goalId: string) => Promise<void>;
  toggleFavorite: (goalId: string) => Promise<void>;
  addSubGoal: (goalId: string, title: string, description: string) => Promise<void>;
  updateSubGoal: (goalId: string, subGoalId: string, updates: Partial<SubGoal>) => Promise<void>;
  deleteSubGoal: (goalId: string, subGoalId: string) => Promise<void>;
  updateTodo: (subGoalId: string, todoId: string, data: Partial<Todo>) => Promise<void>;
  navigateToTab: (tab: TabType) => void;
  openGoalForm: () => void;
  openAIModal: () => void;
  openGoalCreationModal: () => void;
  onShowDrafts: () => void;
  onDraftViewPlan: (plan: AIGoalPlan, draftId: string) => void;
  goToGoalsTab: () => void;
}

const AppRoutes: React.FC<AppRoutesProps> = ({
  route,
  activeTab,
  goals,
  loading,
  error,
  loadGoals,
  updateGoal,
  deleteGoal,
  toggleFavorite,
  addSubGoal,
  updateSubGoal,
  deleteSubGoal,
  updateTodo,
  navigateToTab,
  openGoalForm,
  openAIModal,
  openGoalCreationModal,
  onShowDrafts,
  onDraftViewPlan,
  goToGoalsTab,
}) => {
  if (route.type === 'todo-detail') {
    return <TodoDetailPage />;
  }

  if (route.type === 'subgoal-todos') {
    return <SubGoalTodosPage goals={goals} onGoalTreeChanged={() => { void loadGoals(); }} />;
  }

  if (route.type === 'profile') {
    return <ProfilePage />;
  }

  if (route.type === 'settings') {
    return <SettingsPage />;
  }

  if (route.type === 'ai-drafts') {
    return (
      <AIPlanDraftsPage
        onBack={() => {
          navigateToTab('goals');
        }}
        onViewPlan={onDraftViewPlan}
        onShowAIModal={openAIModal}
      />
    );
  }

  if (route.type === 'goal-detail') {
    const goal = goals.find(g => g.id === route.goalId) || null;
    return (
      <GoalDetailPage
        goal={goal}
        loading={loading}
        onUpdateGoal={updateGoal}
        onDeleteGoal={deleteGoal}
        onToggleFavorite={toggleFavorite}
        onAddSubGoal={addSubGoal}
        onUpdateSubGoal={updateSubGoal}
        onDeleteSubGoal={deleteSubGoal}
        onUpdateTodo={updateTodo}
        onNavigateToTasks={goToGoalsTab}
        onBackToGoals={() => {
          navigateToTab('goals');
        }}
      />
    );
  }

  switch (activeTab) {
    case 'goals':
      return (
        <GoalsPage
          goals={goals}
          loading={loading}
          error={error}
          onLoadGoals={loadGoals}
          onShowForm={openGoalForm}
          onShowAIModal={openAIModal}
          onShowDrafts={onShowDrafts}
          onShowCreationModal={openGoalCreationModal}
          onUpdateGoal={updateGoal}
          onToggleFavorite={toggleFavorite}
          onAddSubGoal={addSubGoal}
          onUpdateSubGoal={updateSubGoal}
          onDeleteSubGoal={deleteSubGoal}
          onUpdateTodo={updateTodo}
        />
      );
    case 'ongoing':
      return <OngoingPage goals={goals} loading={loading} error={error} onTimerChanged={() => { void loadGoals(); }} />;
    case 'timely':
      return <TimelyPage />;
    case 'achievements':
      return <AchievementsPage goals={goals} />;
    default:
      return (
        <HomePage
          goals={goals}
          loading={loading}
          onUpdateGoal={updateGoal}
          onToggleFavorite={toggleFavorite}
          onAddSubGoal={addSubGoal}
          onUpdateSubGoal={updateSubGoal}
          onDeleteSubGoal={deleteSubGoal}
          onUpdateTodo={updateTodo}
          onNavigateToTasks={goToGoalsTab}
          onShowAIModal={openAIModal}
          onShowForm={openGoalForm}
          onShowCreationModal={openGoalCreationModal}
          onTimerChanged={() => { void loadGoals(); }}
        />
      );
  }
};

export default AppRoutes;
